import React from 'react';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import IconButton from '@mui/material/IconButton';

import { Button } from 'components/Button';
import routes from 'core/routes';
import { SLASH_FULL_DATETIME_FORMAT } from 'core/constants';
import { useStore } from 'utils/hooks/store';
import { useNavBarHeight } from 'utils/hooks/navbar';
import { formatMoment } from 'utils/formatters/timezone';
import { AcknowledgementReceiptResponse } from 'utils/api/modules/entities/entities.types';

import { CrumbLink, CrumbRow, CrumbSeparator, DocTitle, Header, PageContainer } from '../styles';
import { NoticeHeadline, NoticeText, NoticeWrapper, ReferenceChip, ReferenceValue } from './styles';

export const ACKNOWLEDGEMENTS_TAB_LINK = routes.dashboard.tasks.acknowledgements.toString();

export type AcknowledgementReceiptProps = {
  data: AcknowledgementReceiptResponse;
  embedded?: boolean;
};

const AcknowledgementReceipt = ({ data, embedded = false }: AcknowledgementReceiptProps): React.ReactElement => {
  const navigate = useNavigate();
  const { notification } = useStore();
  const navBarHeight = useNavBarHeight();

  const handleCopy = (): void => {
    navigator.clipboard.writeText(data.uuid).then(
      () => notification.enqueueInfoSnackbar('Reference copied'),
      () => notification.enqueueErrorSnackbar('Could not copy the reference')
    );
  };

  return (
    <PageContainer $navBarHeight={navBarHeight}>
      <Header>
        {!embedded && (
          <CrumbRow aria-label="breadcrumb">
            <CrumbLink to={String(routes.dashboard.home)}>Home</CrumbLink>
            <CrumbSeparator>›</CrumbSeparator>
            <CrumbLink to={ACKNOWLEDGEMENTS_TAB_LINK}>Actions</CrumbLink>
          </CrumbRow>
        )}
        <DocTitle>{data.document.name}</DocTitle>
      </Header>
      <NoticeWrapper>
        <CheckCircleIcon color="success" sx={{ fontSize: 56 }} />
        <NoticeHeadline>You&apos;ve already acknowledged this document</NoticeHeadline>
        <NoticeText>
          You confirmed you&apos;ve read and understood <strong>{data.document.name}</strong> on{' '}
          <strong data-testid="acknowledgement-acknowledged-at">
            {formatMoment(data.acknowledgedAt, SLASH_FULL_DATETIME_FORMAT)}
          </strong>
          .
        </NoticeText>
        <ReferenceChip>
          <ReferenceValue aria-label="Acknowledgement reference">{data.uuid}</ReferenceValue>
          <IconButton size="small" aria-label="Copy reference" onClick={handleCopy}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </ReferenceChip>
        {!embedded && (
          <Button variant="contained" size="large" onClick={() => navigate(ACKNOWLEDGEMENTS_TAB_LINK)}>
            Back to Actions
          </Button>
        )}
      </NoticeWrapper>
    </PageContainer>
  );
};

export default observer(AcknowledgementReceipt);
